import { Stack, Typography, Button } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { useActiveAddress } from "arweave-wallet-kit"
import { useMemo, useState } from "react"

import type { Pool } from "@/api/pools-api"
import { getUserLockedTokens, withdrawTokens } from "@/api/tokenlocker-api"
import { TokenAmountBlock } from "@/components/TokenAmountBlock"
import { formatFullDate } from "@/utils/format"

export function Withdraw({ pool }: { pool: Pool }) {
  const activeAddr = useActiveAddress()
  const [loading, setLoading] = useState(false)

  const { data: lpBalanceLocked, refetch: refetchLpBalanceLocked } = useQuery({
    queryKey: [`lpBalanceLocked`, activeAddr],
    enabled: !!activeAddr,
    queryFn: async () => {
      if (!activeAddr) return undefined
      const balance = await getUserLockedTokens(activeAddr!, pool.id)
      console.log("onLpBalanceLocked", balance)
      return balance
    },
  })

  const [totalLocked, availableToWithdraw, nextUnlockDate] = useMemo(() => {
    if (!lpBalanceLocked) return [0n, 0n, 0]
    let total = 0n
    let available = 0n
    let soonestDate = Number.MAX_SAFE_INTEGER

    const currentTime = Date.now()

    for (const lock of lpBalanceLocked) {
      total += BigInt(lock.Amount)
      if (Number(lock.End) < currentTime) {
        available += BigInt(lock.Amount)
      } else if (soonestDate > Number(lock.End)) {
        soonestDate = Number(lock.End)
      }
    }
    return [total, available, soonestDate]
  }, [lpBalanceLocked])

  const handleWithdraw = async () => {
    if (availableToWithdraw <= 0) return
    setLoading(true)
    try {
      await withdrawTokens(pool.id, availableToWithdraw)
      refetchLpBalanceLocked()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Typography component={Stack} spacing={2} variant="body2">
      <Stack direction="row" justifyContent={"space-between"}>
        <Typography variant="body2" color="secondary" component="span">
          Total Locked
        </Typography>
        <TokenAmountBlock
          amount={totalLocked}
          tokenId={pool.id}
          needsParsing
          showTicker
          hideCopyToClipboard
        />
      </Stack>
      {nextUnlockDate !== 0 && nextUnlockDate < Number.MAX_SAFE_INTEGER && (
        <Stack direction="row" justifyContent={"space-between"}>
          <Typography variant="body2" color="secondary" component="span">
            Next Unlock Date
          </Typography>
          {formatFullDate(nextUnlockDate)}
        </Stack>
      )}
      <Stack direction="row" justifyContent={"space-between"}>
        <Typography variant="body2" color="secondary" component="span">
          Available to Withdraw
        </Typography>
        <TokenAmountBlock
          amount={availableToWithdraw}
          tokenId={pool.id}
          needsParsing
          showTicker
          hideCopyToClipboard
        />
      </Stack>
      <Button
        variant="contained"
        disabled={loading || availableToWithdraw <= 0}
        onClick={handleWithdraw}
      >
        {loading ? "Withdrawing..." : "Withdraw"}
      </Button>
    </Typography>
  )
}
